'use client';

import { providerMap } from 'auth';
import { Button } from '@components/common/Forms/Button/Button';
import { GoogleMulticolorIcon, FacebookIcon, LinkedInIcon } from '@components/common/Icons/Brands';
import { oAuthAction } from '@auth/actions/oAuthAction';
import { BaseAuthForm } from '@auth/forms/BaseAuthForm';

/** Props */
interface ProvidersLoginProps {
  locale: Locale;
  className?: string;
}

/**
 * @description Providers (Google, Facebook, etc.) login component.
 * - `locale`: The locale to use for translations.
 * - `className`: Optional class name to pass to the component. It will override the default class name.
 */
export const OAuthProvidersForm = (props: ProvidersLoginProps) => {
  // Destructure props
  const {
    locale,
    className = '',
  } = props;

  // ICONS
  // *************************
  const iconClass = 'size-5.5';

  // Oauth providers icons
  const providersIcons = {
    google: <GoogleMulticolorIcon className={iconClass} />,
    facebook: <FacebookIcon className={`${iconClass} size-6 text-social-facebook`} />,
    linkedin: <LinkedInIcon className={`${iconClass} text-social-linkedin`} />,
  };

  // Render content
  // *************************
  return (
    <div className={className || 'flex flex-wrap justify-center gap-3'}>
      {Object.values(providerMap).map((provider) => (
        <BaseAuthForm
          key={provider.id}
          locale={locale}
          oAuthProvider={provider.id}
          formAction={oAuthAction}
          withClientValidation={false}
          className="inline"
        >
          {({ formState, isPending }) => (
            <>
              {/* Button for each OAuth provider */}
              <Button
                type="submit"
                rounding="full"
                styling="outline"
                color="light"
                className="size-13 shrink-0 !border-ink/15 !p-0 hover:!border-ink/5"
                spinnerClass="size-6"
                isLoading={isPending}
              >
                {!isPending && (providersIcons[(provider.id.toLowerCase() as keyof typeof providersIcons)] ?? null)}
              </Button>

              {/* Display a general authentication error if any */}
              {formState?.errorText && (
                <div className="mt-2 rounded bg-error/10 p-2 text-sm text-error">
                  {`${provider.name}: ${formState.errorText}`}
                </div>
              )}
            </>
          )}
        </BaseAuthForm>
      ))}
    </div>
  );
};
